import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DeviceModelOutput,
  CreateDeviceModelInput,
  UpdateDeviceModelInput,
} from '@grosync/common';
import {
  fetchDeviceModels,
  createDeviceModel,
  updateDeviceModel,
  deleteDeviceModel,
} from '../../api/deviceModels';

const KEY = ['device-models'] as const;

export function useDeviceModels(): {
  models: DeviceModelOutput[];
  isLoading: boolean;
  create: (body: CreateDeviceModelInput) => Promise<DeviceModelOutput>;
  update: (id: string, body: UpdateDeviceModelInput) => Promise<DeviceModelOutput>;
  remove: (id: string) => Promise<void>;
} {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: fetchDeviceModels });
  const invalidate = () => void qc.invalidateQueries({ queryKey: KEY });

  const createM = useMutation({ mutationFn: createDeviceModel, onSuccess: invalidate });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDeviceModelInput }) =>
      updateDeviceModel(id, body),
    onSuccess: invalidate,
  });
  const removeM = useMutation({ mutationFn: deleteDeviceModel, onSuccess: invalidate });

  return {
    models: query.data ?? [],
    isLoading: query.isLoading,
    create: (body) => createM.mutateAsync(body),
    update: (id, body) => updateM.mutateAsync({ id, body }),
    remove: (id) => removeM.mutateAsync(id),
  };
}
